import { useState, useCallback, useRef, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { vocabAPI } from '../api/client'

interface Props {
  text: string
  contentId: string
  /** Pass the audio element ref to enable timestamp navigation */
  audioRef?: React.RefObject<HTMLAudioElement>
  /** Total audio duration in seconds (for estimated timestamp calculation) */
  audioDuration?: number
}

interface WordDef {
  word: string
  phonetic: string | null
  partOfSpeech: string | null
  meaning: string | null
  example: string | null
}

interface SelectedWord {
  word: string
  sentence: string
  def: WordDef | null
  loading: boolean
}

/**
 * FR-005 — Interactive Transcript (full implementation)
 *
 * Features:
 * - Click any word → fetch definition from Free Dictionary API + save to vocab
 * - Academic Word List (AWL) words highlighted in amber automatically
 * - Click a sentence → seek audio to estimated timestamp
 * - Saved words shown with green underline
 */
export default function InteractiveTranscript({ text, contentId, audioRef, audioDuration }: Props) {
  const [selected, setSelected] = useState<SelectedWord | null>(null)
  const [savedWords, setSavedWords] = useState<Set<string>>(new Set())
  const [activeSentenceIdx, setActiveSentenceIdx] = useState<number | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const saveWord = useMutation({
    mutationFn: ({ word, sentence }: { word: string; sentence: string }) =>
      vocabAPI.save(word, sentence, contentId),
    onSuccess: (_, vars) => {
      setSavedWords((prev) => new Set(prev).add(vars.word.toLowerCase()))
      setSelected(null)
    },
  })

  const handleWordClick = useCallback(async (word: string, sentence: string) => {
    const clean = word.replace(/[^a-zA-Z'-]/g, '')
    if (!clean || clean.length < 2) return

    setSelected({ word: clean, sentence, def: null, loading: true })

    try {
      const def = await vocabAPI.define(clean)
      setSelected((prev) => prev ? { ...prev, def, loading: false } : null)
    } catch {
      setSelected((prev) => prev ? { ...prev, loading: false } : null)
    }
  }, [])

  // Seek audio to estimated position when clicking a sentence
  const handleSentenceClick = useCallback((sentenceIdx: number, totalSentences: number) => {
    if (!audioRef?.current || !audioDuration) return
    // Estimate: sentences are roughly evenly distributed through the audio
    const estimatedTime = (sentenceIdx / totalSentences) * audioDuration
    audioRef.current.currentTime = Math.max(0, estimatedTime)
    setActiveSentenceIdx(sentenceIdx)
    // Auto-play if paused
    if (audioRef.current.paused) {
      audioRef.current.play().catch(() => {})
    }
  }, [audioRef, audioDuration])

  // Close panel on outside click
  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setSelected(null)
      }
    }
    if (selected) document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [selected])

  const sentences = splitIntoSentences(text)
  const hasTimestamp = !!(audioRef?.current !== undefined && audioDuration)

  return (
    <div className="transcript-section" style={{ position: 'relative' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 800, color: '#272e42' }}>
          Transcript — click từ để xem nghĩa
        </h3>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* AWL legend */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.75rem', color: '#535b71' }}>
            <span style={{ display: 'inline-block', width: '1rem', height: '0.125rem', background: '#b45309', borderRadius: '9999px' }} />
            Từ học thuật (AWL)
          </div>
          {hasTimestamp && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.75rem', color: '#535b71' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '0.875rem', color: '#004be2' }}>touch_app</span>
              Click câu → nhảy audio
            </div>
          )}
        </div>
      </div>

      {/* Transcript body — sentence by sentence */}
      <div className="transcript-text" style={{ lineHeight: 1.9, fontSize: '0.9375rem' }}>
        {sentences.map((sentence, sIdx) => (
          <SentenceBlock
            key={sIdx}
            sentence={sentence}
            sentenceIdx={sIdx}
            totalSentences={sentences.length}
            isActive={activeSentenceIdx === sIdx}
            savedWords={savedWords}
            canSeek={hasTimestamp}
            onWordClick={handleWordClick}
            onSentenceClick={handleSentenceClick}
          />
        ))}
      </div>

      {/* Word definition panel */}
      {selected && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.15)' }}
            onClick={() => setSelected(null)}
          />
          <div
            ref={panelRef}
            className="word-panel"
            style={{ zIndex: 50 }}
          >
            <div className="word-panel-header">
              <div>
                <div className="word-panel-word">
                  {selected.def?.word ?? selected.word}
                  {isAWL(selected.word) && (
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.625rem', fontWeight: 800, padding: '0.125rem 0.5rem', borderRadius: '9999px', background: 'rgba(180,83,9,0.12)', color: '#b45309', verticalAlign: 'middle', letterSpacing: '0.06em' }}>
                      AWL
                    </span>
                  )}
                </div>
                {selected.def?.phonetic && (
                  <div className="word-panel-phonetic">{selected.def.phonetic}</div>
                )}
              </div>
              <button className="btn-close-panel" onClick={() => setSelected(null)}>✕</button>
            </div>

            {selected.loading && (
              <p style={{ color: 'var(--color-muted)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="spinner" style={{ width: '1rem', height: '1rem' }} />
                Đang tra từ điển...
              </p>
            )}

            {!selected.loading && selected.def && (
              <>
                {selected.def.partOfSpeech && (
                  <span className="word-panel-pos">{selected.def.partOfSpeech}</span>
                )}
                {selected.def.meaning && (
                  <p className="word-panel-meaning">{selected.def.meaning}</p>
                )}
                {selected.def.example && (
                  <p className="word-panel-example">"{selected.def.example}"</p>
                )}
              </>
            )}

            {!selected.loading && !selected.def?.meaning && (
              <p style={{ color: 'var(--color-muted)' }}>Không tìm thấy định nghĩa.</p>
            )}

            <div className="word-panel-context">
              <strong>Ngữ cảnh:</strong> {selected.sentence}
            </div>

            <div className="word-panel-actions">
              <button
                className="btn-save-word"
                onClick={() => saveWord.mutate({ word: selected.word, sentence: selected.sentence })}
                disabled={saveWord.isPending || savedWords.has(selected.word.toLowerCase())}
              >
                {savedWords.has(selected.word.toLowerCase())
                  ? '✅ Đã lưu'
                  : saveWord.isPending
                  ? 'Đang lưu...'
                  : '📚 Lưu vào từ vựng'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Sentence Block ───────────────────────────────────────────────────────────

function SentenceBlock({
  sentence, sentenceIdx, totalSentences, isActive,
  savedWords, canSeek, onWordClick, onSentenceClick,
}: {
  sentence: string
  sentenceIdx: number
  totalSentences: number
  isActive: boolean
  savedWords: Set<string>
  canSeek: boolean
  onWordClick: (word: string, sentence: string) => void
  onSentenceClick: (idx: number, total: number) => void
}) {
  const tokens = tokenize(sentence)

  return (
    <span
      onClick={canSeek ? () => onSentenceClick(sentenceIdx, totalSentences) : undefined}
      style={{
        display: 'inline',
        background: isActive ? 'rgba(0,75,226,0.08)' : 'transparent',
        borderRadius: '0.25rem',
        cursor: canSeek ? 'pointer' : 'default',
        transition: 'background 0.2s',
        padding: isActive ? '0.125rem 0.25rem' : '0',
      }}
      title={canSeek ? 'Click để nhảy đến vị trí này trong audio' : undefined}
    >
      {tokens.map((tok, i) => {
        if (!tok.isWord) return <span key={i}>{tok.text}</span>

        const lower = tok.text.toLowerCase()
        const isSaved = savedWords.has(lower)
        const isAcademic = isAWL(lower)

        return (
          <span
            key={i}
            onClick={e => {
              e.stopPropagation() // don't trigger sentence click
              onWordClick(tok.text, sentence)
            }}
            style={{
              cursor: 'pointer',
              borderBottom: isSaved
                ? '2px solid #006571'
                : isAcademic
                ? '2px solid #b45309'
                : 'none',
              color: isSaved ? '#006571' : 'inherit',
              fontWeight: isAcademic && !isSaved ? 600 : 'inherit',
              borderRadius: '0.125rem',
              transition: 'background 0.15s',
              padding: '0 0.05rem',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,75,226,0.08)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
            title={isAcademic ? 'Academic Word List' : undefined}
          >
            {tok.text}
          </span>
        )
      })}
      {' '}
    </span>
  )
}

// ─── Academic Word List (AWL) — Coxhead 570 word families ────────────────────
// Source: Averil Coxhead's Academic Word List (public domain)
// Only the base/root form is stored; matching is done on stemmed form

const AWL_ROOTS = new Set([
  'abandon','abstract','academy','access','accommodate','accompany','accumulate',
  'accurate','achieve','acknowledge','acquire','adapt','adequate','adjacent',
  'adjust','administrate','adult','advocate','affect','aggregate','aid',
  'albeit','allocate','alter','ambiguous','amend','analogy','analyze','annual',
  'anticipate','apparent','append','appreciate','approach','appropriate',
  'approximate','arbitrary','area','aspect','assemble','assess','assign',
  'assist','assume','assure','attach','attain','attitude','attribute',
  'author','automate','available','aware',
  'behalf','benefit','bias','bond','brief','bulk',
  'capable','capacity','category','cease','challenge','channel','chapter',
  'chart','chemical','circumstance','cite','civil','clarify','classic',
  'clause','code','coherent','coincide','collapse','colleague','commence',
  'comment','commit','communicate','community','compatible','compensate',
  'compile','complement','complex','component','comprehensive','comprise',
  'compute','conceive','concentrate','concept','conclude','conduct','confer',
  'confine','confirm','conflict','conform','consent','consequent','considerable',
  'consist','constant','constitute','constrain','construct','consult','consume',
  'contact','contemporary','context','contract','contradict','contrary',
  'contribute','controversy','convene','convert','convince','cooperate',
  'coordinate','core','corporate','correspond','create','criteria','culture',
  'cycle',
  'data','debate','define','demonstrate','derive','design','despite',
  'detect','deviate','device','differentiate','dimension','diminish','discrete',
  'discriminate','display','distribute','document','dominate','draft',
  'dynamic',
  'economy','edit','element','eliminate','emerge','emphasis','enable',
  'encounter','energy','enforce','enhance','environment','equate','establish',
  'estimate','evaluate','evident','evolve','exclude','exhibit','expand',
  'expert','explicit','exploit','expose','external',
  'facilitate','factor','feature','final','finance','flexible','focus',
  'format','formula','foundation','function','fundamental',
  'generate','global','goal','grant','guarantee','guideline',
  'hence','hierarchy','hypothesis',
  'identify','ignore','illustrate','impact','implement','imply','impose',
  'incentive','incline','indicate','individual','induce','inevitable',
  'infer','infrastructure','initiate','innovate','input','instance',
  'integrate','interact','interpret','interval','invest','involve','isolate',
  'issue',
  'justify',
  'label','layer','legal','legislate','link','locate',
  'maintain','major','manipulate','maximize','mechanism','method','minimize',
  'modify','monitor','motivate',
  'network','neutral','norm',
  'obtain','obvious','occur','option','orient','outcome','output','overall',
  'overlap',
  'parallel','participate','perceive','period','persist','phase','phenomenon',
  'policy','potential','predict','principle','prior','proceed','process',
  'professional','prohibit','promote','proportion','publish','pursue',
  'range','react','recover','refine','regulate','reinforce','reject',
  'relevant','rely','remove','require','research','resolve','resource',
  'respond','restrict','retain','reveal','revenue','revise','role',
  'section','sector','secure','seek','select','sequence','significant',
  'similar','simulate','source','specific','stable','strategy','structure',
  'submit','substitute','sufficient','summarize','survey','sustain',
  'target','task','technique','technology','theory','transfer','transform',
  'trend',
  'ultimate','undergo','undertake','uniform','unique','utilize',
  'valid','vary','version','visible',
  'welfare',
])

/** Check if a word (or its simple stem) is in the AWL */
function isAWL(word: string): boolean {
  const w = word.toLowerCase().replace(/[^a-z]/g, '')
  if (AWL_ROOTS.has(w)) return true
  // Simple suffix stripping for common inflections
  const stems = [
    w.replace(/tion$/, 'te').replace(/tion$/, ''),
    w.replace(/ations?$/, 'ate'),
    w.replace(/ments?$/, ''),
    w.replace(/ness$/, ''),
    w.replace(/ity$/, ''),
    w.replace(/ies$/, 'y'),
    w.replace(/ing$/, ''),
    w.replace(/ed$/, ''),
    w.replace(/s$/, ''),
    w.replace(/ly$/, ''),
    w.replace(/er$/, ''),
    w.replace(/ive$/, ''),
    w.replace(/al$/, ''),
  ]
  return stems.some(s => s.length >= 3 && AWL_ROOTS.has(s))
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface Token { text: string; isWord: boolean }

function tokenize(text: string): Token[] {
  return text.split(/(\b[a-zA-Z][a-zA-Z'-]*[a-zA-Z]\b|[a-zA-Z])/).map((t) => ({
    text: t,
    isWord: /^[a-zA-Z][a-zA-Z'-]*$/.test(t) && t.length >= 2,
  }))
}

/** Split transcript into sentences, keeping punctuation attached */
function splitIntoSentences(text: string): string[] {
  // Split on sentence-ending punctuation followed by space/newline
  const raw = text.split(/(?<=[.!?])\s+/)
  return raw.map(s => s.trim()).filter(Boolean)
}
